import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, CheckCircle2, FileText, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import type { AccountantInboxObligationItem } from "@fiscalo/shared-types";

import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { bandejaKeys } from "@/lib/queryKeys";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import {
  accountantBulkMarkObligationsPaid,
  getAccountantInboxObligations,
} from "@/services/dashboardService";
import {
  BandejaRow,
  BandejaRowList,
} from "@/components/features/accountant/BandejaRow";
import { BANDEJA_SECTIONS } from "@/components/features/accountant/BandejaRowMeta";
import {
  BANDEJA_TENANT_FILTER_ALL,
  BandejaFilterBar,
} from "@/components/features/accountant/BandejaFilterBar";

const PAGE_LIMIT_INITIAL = 25;
const PAGE_LIMIT_MAX = 100;

/** Stable client-side key — same shape the bulk endpoint expects. */
function keyOf(tenantId: string, obligationId: string): string {
  return `${tenantId}:${obligationId}`;
}

export function BandejaObligationsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();
  const [limit, setLimit] = useState(PAGE_LIMIT_INITIAL);
  const [searchParams, setSearchParams] = useSearchParams();
  /** Set of `${tenantId}:${obligationId}` keys currently selected. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Keys whose row is fading out during a bulk mark-paid burst.
   *  Driven separately from `selected` so rows animate cleanly even
   *  when React Query refetches and clears the selection. */
  const [animatingOut, setAnimatingOut] = useState<Set<string>>(new Set());
  /** Window param survives in URL too — accountants jumping between
   *  "7d", "30d" and "90d" want the choice to stick on reload. */
  const days = parseInt(searchParams.get("days") ?? "30", 10) || 30;
  const { activeTenant } = useTenantSwitcher();
  // Auto-scope to the active/impersonated client; "Todos" (?tenant=all) opts
  // back into the cartera-wide view. See bandeja-acks-page for rationale.
  const defaultTenantFilter = activeTenant?.id ?? BANDEJA_TENANT_FILTER_ALL;
  const tenantFilter = searchParams.get("tenant") ?? defaultTenantFilter;
  const overdueOnly = searchParams.get("overdue") === "true";
  const searchQuery = searchParams.get("q") ?? "";

  const { data, isLoading } = useQuery({
    queryKey: bandejaKeys.obligations({ limit, days, tenant: tenantFilter }),
    queryFn: () =>
      getAccountantInboxObligations({
        limit,
        days,
        tenantId:
          tenantFilter === BANDEJA_TENANT_FILTER_ALL ? undefined : tenantFilter,
      }),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const items = data?.items ?? [];

  const tenantOptions = useMemo(() => {
    const seen = new Map<string, AccountantInboxObligationItem["tenant"]>();
    for (const it of items) {
      if (!seen.has(it.tenant.id)) seen.set(it.tenant.id, it.tenant);
    }
    return Array.from(seen.values()).sort((a, b) =>
      (a.legalName ?? a.name ?? "").localeCompare(b.legalName ?? b.name ?? ""),
    );
  }, [items]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (
        tenantFilter !== BANDEJA_TENANT_FILTER_ALL &&
        it.tenant.id !== tenantFilter
      ) {
        return false;
      }
      if (overdueOnly && !it.overdue) return false;
      if (normalizedQuery) {
        const haystack = [
          it.tenant.legalName ?? "",
          it.tenant.name ?? "",
          it.tenant.rut ?? "",
          it.taxType,
          it.authority,
          it.period,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(normalizedQuery)) return false;
      }
      return true;
    });
  }, [items, tenantFilter, overdueOnly, normalizedQuery]);

  const hasActiveFilters =
    tenantFilter !== defaultTenantFilter ||
    overdueOnly ||
    normalizedQuery.length > 0;

  // Visible keys drive the "select all" state and the bulk payload —
  // tied to *filtered* items so the bulk action only ever touches what
  // the accountant currently sees (same contract as the ACK queue).
  const visibleKeys = useMemo(
    () => filteredItems.map((i) => keyOf(i.tenant.id, i.obligationId)),
    [filteredItems],
  );
  const effectiveSelectedCount = useMemo(
    () => visibleKeys.filter((k) => selected.has(k)).length,
    [visibleKeys, selected],
  );
  const allVisibleSelected =
    visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));
  const someVisibleSelected =
    !allVisibleSelected && visibleKeys.some((k) => selected.has(k));

  const toggleOne = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllVisible = (): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const k of visibleKeys) next.delete(k);
      } else {
        for (const k of visibleKeys) next.add(k);
      }
      return next;
    });
  };

  const clearSelection = (): void => setSelected(new Set());

  const bulkMutation = useMutation({
    mutationFn: accountantBulkMarkObligationsPaid,
    onMutate: (vars) => {
      const keys = new Set(
        vars.items.map((i) => keyOf(i.tenantId, i.obligationId)),
      );
      setAnimatingOut((prev) => new Set([...prev, ...keys]));
      return { startedKeys: keys };
    },
    onSuccess: async (resp, _vars, ctx) => {
      const paidKeys = new Set(
        resp.results
          .filter((r) => r.status === "paid")
          .map((r) => keyOf(r.tenantId, r.obligationId)),
      );
      // Un-fade rows that came back failed so the accountant can retry.
      if (ctx?.startedKeys) {
        setAnimatingOut((prev) => {
          const next = new Set(prev);
          for (const k of ctx.startedKeys) {
            if (!paidKeys.has(k)) next.delete(k);
          }
          return next;
        });
      }
      setSelected((prev) => {
        const next = new Set(prev);
        for (const k of paidKeys) next.delete(k);
        return next;
      });

      if (resp.paidCount > 0 && resp.failedCount === 0) {
        toast.success(
          t("bandeja.obligations.bulk.allOk", {
            count: resp.paidCount,
            defaultValue: "Marcaste {{count}} obligación como pagada",
            defaultValue_plural: "Marcaste {{count}} obligaciones como pagadas",
          }),
        );
      } else if (resp.paidCount > 0 && resp.failedCount > 0) {
        toast(
          t("bandeja.obligations.bulk.partial", {
            ok: resp.paidCount,
            failed: resp.failedCount,
            defaultValue: "Marcaste {{ok}}; {{failed}} no se pudieron procesar",
          }),
        );
      } else {
        toast.error(
          t(
            "bandeja.obligations.bulk.allFailed",
            "No pudimos marcar las obligaciones seleccionadas",
          ),
        );
      }

      await queryClient.invalidateQueries({ queryKey: bandejaKeys.all });
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.startedKeys) {
        setAnimatingOut((prev) => {
          const next = new Set(prev);
          for (const k of ctx.startedKeys) next.delete(k);
          return next;
        });
      }
      toast.error(
        t(
          "bandeja.obligations.bulk.networkError",
          "No pudimos conectar con el servidor. Probá de nuevo.",
        ),
      );
    },
  });

  const triggerBulkMarkPaid = (amountPaidUyu: number): void => {
    if (effectiveSelectedCount === 0 || bulkMutation.isPending) return;
    const items = filteredItems
      .filter((i) => selected.has(keyOf(i.tenant.id, i.obligationId)))
      .map((i) => ({ tenantId: i.tenant.id, obligationId: i.obligationId }));
    if (items.length === 0) return;
    bulkMutation.mutate({ items, amountPaidUyu });
  };

  const setDaysWindow = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "30") next.delete("days");
          else next.set("days", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setTenantFilter = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Explicit write so "Todos" persists over the active-tenant default.
          next.set("tenant", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleOverdueFilter = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (overdueOnly) next.delete("overdue");
        else next.set("overdue", "true");
        return next;
      },
      { replace: true },
    );
  }, [overdueOnly, setSearchParams]);

  const setSearchQuery = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value.trim() === "") next.delete("q");
          else next.set("q", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tenant");
        next.delete("overdue");
        next.delete("q");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  if (isLoading || !data) return <LoadingPage />;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t(
          "bandeja.obligations.eyebrow",
          "Bandeja · vencimientos",
        )}
        title={BANDEJA_SECTIONS.obligations.title}
        description={BANDEJA_SECTIONS.obligations.description}
        onBack={() => navigate("/accountant/inbox")}
      />

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Ventana
        </span>
        <Select value={String(days)} onValueChange={setDaysWindow}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Próximos 7 días</SelectItem>
            <SelectItem value="30">Próximos 30 días</SelectItem>
            <SelectItem value="90">Próximos 90 días</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card className="border-[var(--fx-accent-300)]/40 bg-[var(--fx-accent-100)]/30">
            <CardContent className="flex items-center gap-3 py-8">
              <CheckCircle2
                className="h-5 w-5 text-[var(--fx-accent-500)]"
                aria-hidden
              />
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Sin vencimientos
                </div>
                <p className="text-xs text-muted-foreground">
                  No hay obligaciones de tu cartera con vencimiento en la
                  ventana seleccionada.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <>
          <BandejaFilterBar
            tenantOptions={tenantOptions}
            tenantFilter={tenantFilter}
            onTenantFilterChange={setTenantFilter}
            overdueOnly={overdueOnly}
            onToggleOverdue={toggleOverdueFilter}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            searchPlaceholder={t(
              "bandeja.obligations.filters.searchPlaceholder",
              "Buscar cliente, impuesto, período…",
            )}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            overdueLabel={t(
              "bandeja.obligations.filters.overdue",
              "Vencidos",
            )}
            reduceMotion={Boolean(reduceMotion)}
          />

          {filteredItems.length === 0 ? (
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)]/30">
                <CardContent className="flex items-center justify-between gap-3 py-6">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4" aria-hidden />
                    {t(
                      "bandeja.obligations.filterEmpty",
                      "Ningún vencimiento coincide con los filtros activos.",
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-8"
                  >
                    {t("bandeja.filters.clear", "Limpiar filtros")}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <Card>
              <div className="flex items-center justify-between border-b border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)]/40 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={
                      allVisibleSelected
                        ? true
                        : someVisibleSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={toggleAllVisible}
                    aria-label={t(
                      "bandeja.obligations.selectAll",
                      "Seleccionar todos los visibles",
                    )}
                  />
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                    <span>
                      {hasActiveFilters
                        ? t("bandeja.obligations.showingFiltered", {
                            shown: filteredItems.length,
                            total: items.length,
                            defaultValue:
                              "Mostrando {{shown}} de {{total}} (filtrado)",
                          })
                        : t("bandeja.obligations.showingOfTotal", {
                            shown: items.length,
                            total: data.total,
                            defaultValue: "Mostrando {{shown}} de {{total}}",
                          })}
                    </span>
                  </div>
                </div>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t(
                    "bandeja.obligations.bulk.hint",
                    "Hacé clic en filas para seleccionar",
                  )}
                </span>
              </div>
              <div className="space-y-1 px-3 py-2">
                <BandejaRowList>
                  {filteredItems.map((item) => {
                    const key = keyOf(item.tenant.id, item.obligationId);
                    return (
                      <BandejaRow
                        key={item.obligationId}
                        kind="obligation"
                        item={item}
                        withInlineActions
                        selection={{
                          selected: selected.has(key),
                          onToggle: () => toggleOne(key),
                        }}
                        bulkPending={animatingOut.has(key)}
                      />
                    );
                  })}
                </BandejaRowList>
              </div>
              {data.hasMore && !hasActiveFilters ? (
                <div className="border-t border-[var(--fx-ink-100)] px-4 py-3 text-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setLimit((l) =>
                        Math.min(PAGE_LIMIT_MAX, l + PAGE_LIMIT_INITIAL),
                      )
                    }
                    disabled={limit >= PAGE_LIMIT_MAX}
                  >
                    Cargar más
                  </Button>
                </div>
              ) : null}
            </Card>
          )}
        </>
      )}

      <AnimatePresence>
        {effectiveSelectedCount > 0 ? (
          <BulkMarkPaidBar
            selectedCount={effectiveSelectedCount}
            isWorking={bulkMutation.isPending}
            onConfirm={triggerBulkMarkPaid}
            onClear={clearSelection}
            reduceMotion={Boolean(reduceMotion)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/**
 * Sticky bottom-anchored bulk bar for marking obligations paid. A single
 * shared amount applies to every selected obligation — the obligation has
 * no expected-amount field, and the bulk case is several periods of the
 * same fixed obligation. Heterogeneous amounts stay a per-row flow.
 */
function BulkMarkPaidBar({
  selectedCount,
  isWorking,
  onConfirm,
  onClear,
  reduceMotion,
}: {
  selectedCount: number;
  isWorking: boolean;
  onConfirm: (amountPaidUyu: number) => void;
  onClear: () => void;
  reduceMotion: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");

  const parsedAmount = (() => {
    const value = parseFloat(amount.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(value) && value >= 0 ? value : null;
  })();

  return (
    <motion.div
      initial={reduceMotion ? false : { y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { y: 24, opacity: 0 }}
      transition={{ type: "spring", stiffness: 350, damping: 30 }}
      className="pointer-events-none sticky bottom-4 z-30 mt-2 flex justify-center"
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-[var(--fx-ink-200)] bg-[var(--fx-ink-900)] px-3 py-2 text-[var(--fx-paper)] shadow-[0_18px_44px_-18px_rgba(22,21,20,0.55)]">
        <span className="pl-2 text-sm font-medium tabular-nums">
          {t("bandeja.obligations.bulk.selectedCount", {
            count: selectedCount,
            defaultValue: "{{count}} seleccionada",
            defaultValue_plural: "{{count}} seleccionadas",
          })}
        </span>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="bulk-paid-amount" className="sr-only">
            {t("bandeja.obligations.bulk.amountLabel", "Monto pagado (UYU)")}
          </Label>
          <Input
            id="bulk-paid-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t(
              "bandeja.obligations.bulk.amountPlaceholder",
              "Monto c/u (UYU)",
            )}
            disabled={isWorking}
            className="h-8 w-32 rounded-full border-[var(--fx-ink-200)] bg-[var(--fx-paper)] text-[var(--fx-ink-900)] placeholder:text-[var(--fx-ink-400)] dark:border-[var(--dk-border-2)] dark:bg-[var(--dk-surface)] dark:text-[var(--dk-text)]"
          />
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => parsedAmount != null && onConfirm(parsedAmount)}
          disabled={isWorking || parsedAmount == null}
          className="h-8 gap-1.5 rounded-full bg-[var(--fx-accent-300)] text-[var(--fx-ink-900)] hover:bg-[var(--fx-s-raised-hover)]"
        >
          {isWorking ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {t("bandeja.obligations.bulk.working", "Marcando…")}
            </>
          ) : (
            <>
              <Check className="h-3.5 w-3.5" aria-hidden />
              {t("bandeja.obligations.bulk.markPaid", {
                count: selectedCount,
                defaultValue: "Marcar {{count}} pagadas",
              })}
            </>
          )}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClear}
          disabled={isWorking}
          className="h-8 gap-1 rounded-full text-[var(--fx-paper)]/70 hover:bg-[var(--fx-s-raised-hover)]/10 hover:text-[var(--fx-paper)]"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          {t("bandeja.obligations.bulk.clear", "Limpiar")}
        </Button>
      </div>
    </motion.div>
  );
}
